import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, of, startWith, switchMap } from 'rxjs';

import { EntityInterface } from '@core/models/entities/entity-visualization-interface';
import {
  PlanFilter,
  PlanFilterOperator
} from '@core/models/entity/entity-selector-request.model';
import {
  ReferenceFieldOption,
  ReferenceFieldsService
} from '@core/services/reference-fields/reference-fields.service';
import {
  ReferenceNamesService,
  ReferenceResolutionContext
} from '@core/services/reference-names/reference-names.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { selectReferenceFields } from '@core/store/user-preferences/user-preferences.selectors';
import { isNil } from '@core/utils/shared-utils';

import { AnchoredPopupComponent } from '@shared/components/anchored-popup/anchored-popup.component';
import { ReferenceDetailPopoverComponent } from '@shared/components/reference-detail-popover/reference-detail-popover.component';
import { ReferencePopupService } from '@shared/services/reference-popup/reference-popup.service';
import { IncludeInFilterStatus } from '@shared/utils/include-in-filter.utils';

interface ResolvedReferenceData {
  name: string | undefined;
  record: EntityInterface<unknown> | undefined;
  loading: boolean;
  error: boolean;
}

const IDLE: ResolvedReferenceData = {
  name: undefined,
  record: undefined,
  loading: false,
  error: false
};

/**
 * Smart outlet: reads
 * ReferencePopupService's state, resolves the name/record for the open uid,
 * and renders the dumb ReferenceDetailPopoverComponent inside the shared
 * AnchoredPopupComponent shell.
 */
@Component({
  selector: 'app-reference-popup-outlet',
  imports: [AnchoredPopupComponent, ReferenceDetailPopoverComponent],
  template: `
    @if (state(); as s) {
      <app-anchored-popup
        [anchorEl]="s.anchorEl"
        [width]="320"
        [owner]="popupService"
        (dismiss)="popupService.close()">
        <app-reference-detail-popover
          [name]="resolved().name"
          [uid]="s.data.uid"
          [targetEntityType]="s.data.targetEntityType"
          [availableFields]="availableFields()"
          [selectedFields]="selectedFields()"
          [record]="resolved().record"
          [loading]="resolved().loading"
          [error]="resolved().error"
          [canAddToFilter]="canAddToFilter() && isOpenColumnFilterable()"
          [includeInFilterStatus]="openIncludeInFilterStatus()"
          (changeFields)="onChangeFields($event)"
          (addToFilter)="onAddToFilter()"
          (includeInFilter)="onIncludeInFilter()"
          (dismiss)="popupService.close()" />
      </app-anchored-popup>
    }
  `,
  styles: ``
})
export class ReferencePopupOutletComponent implements OnInit {
  protected readonly popupService = inject(ReferencePopupService);
  private readonly referenceNamesService = inject(ReferenceNamesService);
  private readonly referenceFieldsService = inject(ReferenceFieldsService);
  private readonly selectorsService = inject(SelectorsService);
  private readonly store = inject(Store);

  /**
   * A page outlet is wired to its page's filter actions; the shell outlet is the fallback for
   * pages without one, so it renders nothing while any page outlet is mounted.
   */
  readonly scope = input<'page' | 'shell'>('page');

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    if (this.scope() === 'page') {
      this.destroyRef.onDestroy(this.popupService.registerPageOutlet());
    }
  }

  private readonly popupState = this.popupService.state;
  protected readonly state = computed(() =>
    this.scope() === 'shell' && this.popupService.pageOutlets() > 0
      ? null
      : this.popupState()
  );

  /** Off where the host has nothing to filter — a findings panel is not a table. */
  readonly canAddToFilter = input<boolean>(true);

  readonly addToFilter = output<{ row: unknown; column: string; item: unknown }>();

  /** Unset where the host table has no say: Add to filter then shows for every column. */
  readonly isColumnFilterable = input<((column: string) => boolean) | undefined>(undefined);

  /** Unset where the host has no filter to widen: Include in filter then never shows. */
  readonly includeInFilterStatus = input<
    ((targetEntityType: string, uid: string) => IncludeInFilterStatus) | undefined
  >(undefined);

  readonly includeInFilter = output<{ uid: string; targetEntityType: string }>();

  protected readonly isOpenColumnFilterable = computed(() => {
    const s = this.state();
    return !s || (this.isColumnFilterable()?.(s.data.column) ?? true);
  });

  protected readonly openIncludeInFilterStatus = computed<IncludeInFilterStatus>(() => {
    const s = this.state();
    const status = this.includeInFilterStatus();
    return s && status ? status(s.data.targetEntityType, s.data.uid) : 'hidden';
  });

  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly referenceFieldsPrefs = this.store.selectSignal(selectReferenceFields);

  protected readonly availableFields = computed<ReferenceFieldOption[]>(() => {
    const s = this.state();
    return s ? this.referenceFieldsService.availableFields(s.data.targetEntityType) : [];
  });

  protected readonly selectedFields = computed<string[]>(() => {
    const s = this.state();
    if (!s) return [];
    return this.referenceFieldsPrefs()?.[s.data.targetEntityType] ?? [];
  });

  protected readonly resolved = toSignal(
    toObservable(this.state).pipe(
      switchMap((s) => {
        if (!s) return of(IDLE);
        const ctx = this.context();
        if (!ctx) return of(IDLE);

        const versionName = this.referenceFieldsService.resolveVersionName(
          s.data.targetEntityType
        );
        const name$ = this.referenceNamesService
          .resolveNames$(s.data.targetEntityType, [s.data.uid], ctx)
          .pipe(map((names) => names.get(s.data.uid)));
        const record$ = versionName
          ? this.selectorsService
              .selectEntityByUid$(
                s.data.targetEntityType,
                versionName,
                s.data.uid,
                ctx.dataSpaceId,
                this.planFilter(ctx)
              )
              .pipe(catchError(() => of(undefined)))
          : of(undefined);

        return forkJoin([name$, record$]).pipe(
          map(
            ([name, record]): ResolvedReferenceData => ({
              name,
              record,
              loading: false,
              error: false
            })
          ),
          startWith({ ...IDLE, loading: true }),
          catchError(() => of({ ...IDLE, error: true }))
        );
      })
    ),
    { initialValue: IDLE }
  );

  /** Resolution context (dataspace + plan), mirroring entity.component's referenceContext(). */
  private context(): ReferenceResolutionContext | null {
    const dataSpaceId = this.dataspaceState().dataspace?.id;
    if (isNil(dataSpaceId)) return null;
    const planId = this.plan()?.id;
    return isNil(planId) ? { dataSpaceId } : { dataSpaceId, planId };
  }

  private planFilter(ctx: ReferenceResolutionContext): PlanFilter | undefined {
    return ctx.planId ? { id: ctx.planId, operator: PlanFilterOperator.IN } : undefined;
  }

  protected onChangeFields(fields: string[]): void {
    const s = this.state();
    if (!s) return;
    this.store.dispatch(
      UserPreferencesActions.setReferenceFields({
        entityType: s.data.targetEntityType,
        fields
      })
    );
  }

  protected onIncludeInFilter(): void {
    const s = this.state();
    if (!s) return;
    this.includeInFilter.emit({
      uid: s.data.uid,
      targetEntityType: s.data.targetEntityType
    });
    this.popupService.close();
  }

  protected onAddToFilter(): void {
    const s = this.state();
    if (!s) return;
    this.addToFilter.emit({ row: s.data.row, column: s.data.column, item: s.data.uid });
  }
}
